import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";

const formulaTypesApiPath = "formula-types";

const isFilteredFormulaTypesQuery = (params) => {
    const filterKeys = ['search', 'description', 'type'];
    return filterKeys.some((key) => params[key] !== undefined && params[key] !== null && params[key] !== '');
};

export const GetFormulaTypes = createAsyncThunk("formulaTypes/GetFormulaTypes", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    // Support both calling conventions: GetFormulaTypes(token) or GetFormulaTypes({ token, page, per_page, search })
    let token = null;
    let params = {};
    if (typeof obj === 'string' || obj instanceof String) {
        token = obj;
    } else if (obj && typeof obj === 'object') {
        token = obj.token;
        params = { ...obj };
        delete params.token;
        delete params.catalogOnly;
    }

    const catalogOnly = Boolean(obj && typeof obj === 'object' && obj.catalogOnly);
    const filteredQuery = !catalogOnly && isFilteredFormulaTypesQuery(params);

    try {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&');
        const url = `${config.apiUrl}/${formulaTypesApiPath}${qs ? `?${qs}` : ''}`;


        const response = await fetch(url, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data.error ?? data);
        }

        return fulfillWithValue({ ...data, meta: { catalogOnly, filteredQuery } });
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const AddFormulaType = createAsyncThunk("formulaTypes/AddFormulaType", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        // Support both JSON payloads and FormData for file uploads
        const isFormData = obj.values instanceof FormData;
        const response = await fetch(`${config.apiUrl}/${formulaTypesApiPath}`, {
            method: "POST",
            body: isFormData ? obj.values : JSON.stringify(obj.values),
            headers: {
                Accept: "application/json",
                // When sending FormData, do NOT set Content-Type so browser can add boundaries
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                Authorization: `Bearer ${obj.token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data.error ?? data);
        }

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const UpdateFormulaType = createAsyncThunk("formulaTypes/UpdateFormulaType", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        // Support both JSON payloads and FormData for file uploads
        const isFormData = obj.values instanceof FormData;
        let method = "PUT";
        let body = isFormData ? obj.values : JSON.stringify(obj.values);

        // For multipart updates, use method spoofing so Laravel parses payload/files correctly.
        if (isFormData) {
            method = "POST";
            if (!obj.values.get("_method")) {
                obj.values.append("_method", "PUT");
            }
            body = obj.values;
        }

        const response = await fetch(`${config.apiUrl}/${formulaTypesApiPath}/${obj.id}`, {
            method,
            body,
            headers: {
                Accept: "application/json",
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                Authorization: `Bearer ${obj.token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data.error ?? data);
        }

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const DeleteFormulaTypeAttachment = createAsyncThunk("formulaTypes/DeleteFormulaTypeAttachment", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        const response = await fetch(`${config.apiUrl}/${formulaTypesApiPath}/${obj.id}/attachments/${obj.attachmentId}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${obj.token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data.error ?? data);
        }

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const DeleteFormulaType = createAsyncThunk("formulaTypes/DeleteFormulaType", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        const response = await fetch(`${config.apiUrl}/${formulaTypesApiPath}/${obj.id}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${obj.token}`,
            },
        });

        const data = await response.json();
        if (!response.ok) {
            return rejectWithValue(data.error ?? data);
        }

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const FormulaTypesSlice = createSlice({
    name: "formulaTypes",
    initialState: {
        items: [],
        catalogItems: [],
        loading: false,
        error: null,
        successMessage: null,
        selectedFormulaType: null,
        // Pagination state
        total: 0,
        page: 1,
        perPage: 10,
    },
    reducers: {
        selectFormulaType: (state, action) => {
            state.selectedFormulaType = action.payload;
        },
    },
    extraReducers: {
        [GetFormulaTypes.pending]: (state) => {
            state.loading = true;
            state.successMessage = null;
            state.error = null;
        },
        [GetFormulaTypes.fulfilled]: (state, action) => {
            state.loading = false;
            state.successMessage = null;
            state.error = null;

            const payload = action.payload ?? {};
            const meta = payload.meta ?? {};
            let rows = [];
            // Support paginated and non-paginated responses
            // Paginated: { data: { data: [...], total: X, per_page: Y, current_page: Z } }
            // Non-paginated: { data: [...] }
            if (payload.data && payload.data.data) {
                rows = payload.data.data ?? [];
                state.total = payload.data.total ?? state.total;
                state.page = payload.data.current_page ?? state.page;
                state.perPage = payload.data.per_page ?? state.perPage;
            } else {
                rows = payload.data ?? [];
                state.total = (Array.isArray(payload.data)) ? payload.data.length : state.total;
            }

            if (meta.catalogOnly || !meta.filteredQuery) {
                state.catalogItems = rows;
            }
            if (!meta.catalogOnly) {
                state.items = rows;
            }
        },
        [GetFormulaTypes.rejected]: (state, action) => {
            state.loading = false;
            state.successMessage = null;
            state.error = formatApiError(action.payload, null);
        },

        [AddFormulaType.pending]: (state) => {
            state.loading = true;
            state.successMessage = null;
            state.error = null;
        },
        [AddFormulaType.fulfilled]: (state, action) => {
            state.loading = false;
            state.successMessage = action.payload.message;
            state.error = null;
            const created = action.payload.data;
            if (created) {
                state.items = [...state.items, created];
                state.catalogItems = [...state.catalogItems, created];
            }
        },
        [AddFormulaType.rejected]: (state, action) => {
            state.loading = false;
            state.successMessage = null;
            state.error = formatApiError(action.payload, null);
        },

        [UpdateFormulaType.pending]: (state) => {
            state.loading = true;
            state.successMessage = null;
            state.error = null;
        },
        [UpdateFormulaType.fulfilled]: (state, action) => {
            state.loading = false;
            state.successMessage = action.payload.message;
            state.error = null;
            const updated = action.payload.data;
            if (!updated) {
                return;
            }
            const index = state.items.findIndex((row) => row.id === updated.id);
            if (index !== -1) {
                state.items[index] = {
                    ...state.items[index],
                    ...updated,
                };
            }

            if (state.selectedFormulaType && (state.selectedFormulaType.id === updated.id || state.selectedFormulaType.key === updated.id)) {
                state.selectedFormulaType = updated;
            }
        },

        [DeleteFormulaTypeAttachment.pending]: (state) => {
            state.loading = true;
            state.successMessage = null;
            state.error = null;
        },
        [DeleteFormulaTypeAttachment.fulfilled]: (state, action) => {
            state.loading = false;
            state.successMessage = action.payload.message;
            state.error = null;

            const updated = action.payload.data;
            if (!updated) return;

            if (state.selectedFormulaType && (state.selectedFormulaType.id === updated.id || state.selectedFormulaType.key === updated.id)) {
                state.selectedFormulaType = updated;
            }

            const index = state.items.findIndex((m) => m.id === updated.id);
            if (index !== -1) {
                state.items[index] = {
                    ...state.items[index],
                    ...updated,
                };
            }
        },
        [DeleteFormulaTypeAttachment.rejected]: (state, action) => {
            state.loading = false;
            state.successMessage = null;
            state.error = formatApiError(action.payload, null);
        },
        [UpdateFormulaType.rejected]: (state, action) => {
            state.loading = false;
            state.successMessage = null;
            state.error = formatApiError(action.payload, null);
        },

        [DeleteFormulaType.pending]: (state) => {
            state.loading = true;
            state.successMessage = null;
            state.error = null;
        },
        [DeleteFormulaType.fulfilled]: (state, action) => {
            state.loading = false;
            state.successMessage = action.payload.message;
            state.error = null;
            const removed = action.payload.data;
            if (!removed) {
                return;
            }
            state.items = state.items.filter(({ id }) => id !== removed.id);
        },
        [DeleteFormulaType.rejected]: (state, action) => {
            state.loading = false;
            state.successMessage = null;
            state.error = formatApiError(action.payload, null);
        },
    },
});

export default FormulaTypesSlice.reducer;
export const { selectFormulaType } = FormulaTypesSlice.actions;
